export interface FrontCoverSrcInput {
  overrideUrl?: string | null;
  designUrl?: string | null;
  thumbnailUrl?: string | null;
  storagePathUrl?: string | null;
  hidden?: boolean;
}

export interface FrontCoverSrc {
  url: string | null;
  /** true => render via the transparent-design (contain-fit + transform) path. */
  isDesignPath: boolean;
}

/**
 * Rewrite a Supabase public-storage URL to a small, CDN-cached WebP/AVIF via the
 * image-transform endpoint — for the design-PICKER grid ONLY. The catalogue
 * stores full-res cover art (a transparent décor PNG can be ~1.7 MB); loading
 * ~200 of them at full size is what made the picker slow to open + scroll.
 *
 * The grid card is a fixed 3:4 portrait, so we request a 3:4 `cover` transform
 * at ~card-width×DPR (default 400×534). Supabase negotiates WebP/AVIF from the
 * Accept header and PRESERVES PNG alpha, so the board colour still shows through
 * the transparent décor. Result: ~1.7 MB → ~35 KB, cached by the Supabase CDN +
 * the browser (reopening the picker is instant).
 *
 * Non-Supabase or already-transformed URLs pass through unchanged. The editor +
 * PDF keep the full-res original via `resolveFrontCoverImageSrc`.
 */
export function pickerThumbUrl(url: string | null | undefined, width = 400): string | null | undefined {
  if (!url) return url;
  if (url.includes('/render/image/')) return url; // already a transform URL
  const marker = '/storage/v1/object/public/';
  const i = url.indexOf(marker);
  if (i === -1) return url; // not a Supabase public-storage URL — leave as-is
  const rendered = `${url.slice(0, i)}/storage/v1/render/image/public/${url.slice(i + marker.length)}`;
  const height = Math.round((width * 4) / 3); // grid cards are 3:4 portrait
  const sep = rendered.includes('?') ? '&' : '?';
  return `${rendered}${sep}width=${width}&height=${height}&resize=cover&quality=70`;
}

/**
 * Single source of truth for which image the front cover shows.
 * Priority: hidden > override > design_url > thumbnail_url > storage_path.
 *
 * The per-order admin override and the catalogue `design_url` are both PNGs
 * drawn contain-fit with the customer's translate/scale transform on top
 * (`isDesignPath: true`). `thumbnail_url` / signed `storage_path` are legacy
 * fully-baked images drawn cover-fit (`isDesignPath: false`).
 *
 * Used by the PDF renderer (`drawFrontCover`); `CoverThumbnail` applies the same
 * top-priority rule inline so the editor preview and the PDF cannot diverge.
 */
export function resolveFrontCoverImageSrc(input: FrontCoverSrcInput): FrontCoverSrc {
  if (input.hidden) return { url: null, isDesignPath: false };
  if (input.overrideUrl) return { url: input.overrideUrl, isDesignPath: true };
  if (input.designUrl) return { url: input.designUrl, isDesignPath: true };
  if (input.thumbnailUrl) return { url: input.thumbnailUrl, isDesignPath: false };
  if (input.storagePathUrl) return { url: input.storagePathUrl, isDesignPath: false };
  return { url: null, isDesignPath: false };
}
